import React, { useEffect, useRef, useState } from 'react';
import { agentsColumnDetails, agentFilters, AgentColumnKey, ApiEndpoints, ScanTypes, ScanTypeValues } from '../utils/common';
import { getData, triggerOnDemandAgentSync, uninstallAgents, postData, deleteData, fetchAssets, createScanTaskAgents } from '../utils/apiFunctions';
import { useEventListener } from '../utils/customHooks'
import { ExcludedEnum, ExcludedValues, InstallStates, InstallStatesValues, InventoryEntityType, KaseyaInventoryItemType } from '../utils/enums';
import moment from 'moment'
import { useStateContext } from '../contexts/ContextProvider';

const toastNotification = (message, severity, isStacked = true) => {
  const toastNotificationConfigs = {
    message: message,
    severity: severity,
    x: "right",
    y: "bottom",
    autoCloseDelayMs: 10000,
  };

  window.nirvana.dependencies.services.buildToastNotification(
    toastNotificationConfigs,
    undefined,
    isStacked
  );
};

let gridElement = null;

let agents = {
  data: [],
  totalCount: 0
}

const defaultViewProps = {
  id: '1234',
  name: 'Default Agents View',
  type: 0,
  filters: agentFilters,
  displayedColumns: [0, 1, 2, 3, 4, 5],
  pageSize: 5,
  pageNumber: 1,
  compactView: false
};

const getDependencies = () => {
  return {
    api: {
      type: 'nirvana',
    },
    translateFn: (text, replacements) => {
      if (!replacements || !Object.keys(replacements).length) {
        return text;
      }

      const variables = Object.keys(replacements);
      const variablesTexts = variables.map((variable) => '{' + variable + '}');
      const regex = new RegExp(variablesTexts.join('|'), 'g');

      return text.replace(regex, (matched) => {
        return replacements[matched.slice(1, -1)];
      });
    },
    getTimezone: () => 'UTC',
    getUserLanguage: () => 'en-US',
  };
}

const EndpointsGrid = ({ selectedInventoryNode }) => {
  class DataProvider {

    dependencies;
    allColumnDetails;
    targetIds;
    defaultViewProps;
    hasPagination;

    constructor(dependencies, allColumnDetails, targetIds, defaultTargetIds, hasPagination) {
      this.dependencies = dependencies;
      this.allColumnDetails = allColumnDetails;
      this.targetIds = targetIds;
      this.defaultTargetIds = defaultTargetIds;
      this.hasPagination = hasPagination;
    }

    async getSummary(request) {
      if (selectedInventoryNode?.nodeId === undefined) {
        return Promise.resolve({
          summary: `0 items`,
          numItems: 0,
        });
      }

      agents = await getAgentsForGrid(request, selectedInventoryNode);
      return Promise.resolve({
        summary: `${agents.totalCount} items`,
        numItems: agents.totalCount,
      })
    }

    async getRows(request) {
      gridElement.controller.resetSelection();

      if (selectedInventoryNode?.nodeId === undefined) {
        return Promise.resolve({
          rawEvents: []
        })
      }

      return Promise.resolve({
        rawEvents: agents.data && agents.data.map((agent) => {
          return {
            id: agent["id"],
            name: agent["name"],
            publicIpAddress: agent["publicIpAddress"],
            description: agent["description"],
            type: agent["type"],
            lastSeenOnline: agent["lastSeenOnline"] ?? '-',
            excluded: agent["excluded"],
            installState: agent["installState"]
          }
        })
      })
    }

    async updateSettings() {
      return {};
    }

    async setFilters() {
      return {};
    }

    shouldDataReloadOnResetView() {
      return true;
    }
  }

  const filterByExcludedColumn = (filters, agents) => {
    const excludedFilter = filters.find(x => x["excluded"]);
    const valuesForExclusionFilter = Object.values(excludedFilter)[0]["in"];

    if (valuesForExclusionFilter.length === 0 || valuesForExclusionFilter.length === ExcludedValues.length) {
      return agents;
    }

    return agents && agents.filter(agent => agent.excluded === valuesForExclusionFilter[0]);
  }

  const filterByInstallStateColumn = (filters, filteredAgents) => {
    const installStateFilter = filters.find(x => x["installState"]);
    const valuesForInstallStateFilter = Object.values(installStateFilter)[0]["in"];

    if (valuesForInstallStateFilter.length === 0 || valuesForInstallStateFilter.length === InstallStatesValues.length) {
      return filteredAgents;
    }

    return filteredAgents.filter(agent => valuesForInstallStateFilter.includes(agent.installState));
  }

  const showAgentsBasedOnRmmiFilters = (filters, agents) => {
    if (agents.length === 0) {
      return agents
    }

    let filteredExcludeAgents = filterByExcludedColumn(filters, agents)
    let filteredInstallStateAgents = filterByInstallStateColumn(filters, filteredExcludeAgents)
    return filteredInstallStateAgents;
  }

  const dependencies = getDependencies()

  const provider = new DataProvider(dependencies, agentsColumnDetails, [], [], true);

  const [selectedAgents, setSelectedAgents] = useState([])
  const [areItemsExcluded, setAreItemsExcluded] = useState(false)
  const { isOndemandAgentTaskRunning, isRecurrentAgentTaskRunning } = useStateContext();
  let isAgentTaskRunning = isOndemandAgentTaskRunning || isRecurrentAgentTaskRunning
  let shouldDisableAgentSyncButton = selectedAgents.length === 0 || isAgentTaskRunning || !selectedInventoryNode?.originalData?.isSynchronized

  useEffect(() => {
    gridElement = document.getElementById('grid');
    gridElement.dataProvider = provider
    gridElement.titleConfig = JSON.stringify({ title: 'Kaseya Agents' })
    gridElement.viewId = '1234'
    gridElement.allColumnDetails = agentsColumnDetails
    gridElement.hasSelectableRows = true
    gridElement.defaultViewProps = defaultViewProps
    gridElement.isCompactViewChecked = false
    gridElement.columns = [0, 1, 2, 3, 4, 5, 6]
    gridElement.predefinedFiltersKeys = [AgentColumnKey.AGENT_NAME, AgentColumnKey.EXCLUDED, AgentColumnKey.IP_ADDRESS, AgentColumnKey.OS_INFO, AgentColumnKey.OS, AgentColumnKey.INSTALLSTATE]
    gridElement.filters = agentFilters
    gridElement.pageSize = 20
    gridElement.hasPagination = true
    gridElement.propertiesSet = true
    gridElement.injectDependencies(dependencies);

    gridElement.addEventListener('selectRows', function (event) {
      setAreItemsExcluded(event.detail.selectedItemsData.find(a => a.excluded === ExcludedEnum.ExcludedFromSync))
      setSelectedAgents(event.detail.selectedItemsData.map((item) => { return { 'id': item.id, 'isExcluded': item.excluded === ExcludedEnum.ExcludedFromSync } }))
    })

    gridElement.start().then().catch(console.error);
  }, [selectedInventoryNode]);

  const getAgentsForGrid = async (request, selectedInventoryNode) => {
    let assets = await fetchAssets(request.filters,
      request.pageNumber,
      request.pageSize,
      selectedInventoryNode?.originalData.type,
      selectedInventoryNode?.nodeId,
      selectedInventoryNode?.nodeName,
      selectedInventoryNode?.parentNodeId
    );

    if (assets.totalCount === 0) {
      return {
        data: [],
        totalCount: 0
      }
    }

    const agentExclusions = await getData(`${ApiEndpoints.AGENT_EXCLUSION}`)
    const excludedAgentIds = agentExclusions.map((a) => a.rmmEndpointId)
    const agentInstallStates = await getData(`${ApiEndpoints.AGENT_INSTALL_STATES}`)

    const agentsData = assets.data.map(item => {
      return {
        'id': item.identifier,
        'name': item.name,
        'publicIpAddress': item.publicIpAddress,
        'description': item.description,
        'type': item.type,
        'lastSeenOnline': item.lastSeenOnline && moment(item.lastSeenOnline).format('MMMM Do YYYY, h:mm:ss a'),
        'excluded': excludedAgentIds.includes(item.identifier) ? ExcludedEnum.ExcludedFromSync : ExcludedEnum.IncludedInSync,
        'installState': agentInstallStates.filter(a => a.rmmEndpointId === item.identifier)[0]?.installState ?? InstallStates.NotInstalled
      }
    })

    let filteredAgents = showAgentsBasedOnRmmiFilters(request.filters, agentsData);

    let agentsForGrid = {
      data: filteredAgents,
      totalCount: assets.totalCount
    }
    return agentsForGrid
  }

  const handleSyncAgents = async () => {
    const dialogBody = document.createElement('div');
    const footer = document.createElement('div');

    footer.innerHTML = `
      <gz-button id="syncAgents">Sync Agents</gz-button>
      <gz-button id="cancelAction" type="outline" color="secondary">Cancel</gz-button>
    `;

    dialogBody.innerHTML = `
      <p style="margin-bottom: 1rem;">You are about to start synchronizing the Bitdefender agent on the selected targets. During this process, the agent may be installed or reinstalled as needed.<br>
      This task will only affect synchronized inventories.</p>
    `;

    const syncAgentsButton = footer.querySelector('#syncAgents');
    const cancelActionButton = footer.querySelector('#cancelAction');

    const dialogConfigs = {
      headline: 'Synchronize agents?',
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(dialogConfigs);

    if (syncAgentsButton) {
      syncAgentsButton.addEventListener('click', async () => {
        dialog.dismiss();
        const runSynchronizeEndpointsTaskOnDemand = selectedAgents.map((item) => { return { ContextType: KaseyaInventoryItemType.DEVICE, ItemId: item.id }; });

        await triggerOnDemandAgentSync(runSynchronizeEndpointsTaskOnDemand);
        setTimeout(() => {
          gridElement.controller.handleGridItemsLoad(true, true);
        }, 5000);
      });
    }

    if (cancelActionButton) {
      cancelActionButton.addEventListener('click', () => { dialog.dismiss(); });
    }
  }

  const handleAgentsUninstall = async () => {
    const dialogBody = document.createElement('div');
    const footer = document.createElement('div');

    footer.innerHTML = `
      <gz-button id="uninstallAction" color="negative">Uninstall</gz-button>
      <gz-button id="cancelAction" type="outline" color="secondary">Cancel</gz-button>
    `;

    dialogBody.innerHTML = `
      <p style="margin-bottom: 1rem;">This action will uninstall the Bitdefender agents on the selected endpoints.</p>
    `;

    const uninstallActionButton = footer.querySelector('#uninstallAction');
    const cancelActionButton = footer.querySelector('#cancelAction');

    const dialogConfigs = {
      headline: 'Uninstall Bitdefender agents?',
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(dialogConfigs);

    if (uninstallActionButton) {
      uninstallActionButton.addEventListener('click', async () => {
        dialog.dismiss()
        await uninstallAgents(selectedAgents.map(a => a.id))
        setTimeout(() => {
          gridElement.controller.handleGridItemsLoad(true, true);
        }, 5000);
      });
    }

    if (cancelActionButton) {
      cancelActionButton.addEventListener('click', () => { dialog.dismiss() })
    }
  }

  const excludeAgents = async () => {
    const dialogBody = document.createElement('div');
    const footer = document.createElement('div');

    footer.innerHTML = `
      <gz-button id="excludeAgentsAction">Exclude Agents</gz-button>
      <gz-button id="cancelAction" type="outline" color="secondary">Cancel</gz-button>
    `;

    dialogBody.innerHTML = `
      <p style="margin-bottom: 1rem;">This action will prevent the Bitdefender agents from being installed on the selected endpoints.</p>
    `;

    const excludeAgentsActionButton = footer.querySelector('#excludeAgentsAction');
    const cancelActionButton = footer.querySelector('#cancelAction');

    const dialogConfigs = {
      headline: ' Exclude agents from sync?',
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(dialogConfigs);

    if (excludeAgentsActionButton) {
      excludeAgentsActionButton.addEventListener('click', async () => {
        dialog.dismiss()
        postData(ApiEndpoints.AGENT_EXCLUSION, selectedAgents.map(a => a.id), true)
        setTimeout(() => {
          gridElement.controller.handleGridItemsLoad(true, true);
        }, 1000);
      });
    }

    if (cancelActionButton) {
      cancelActionButton.addEventListener('click', () => { dialog.dismiss() })
    }
  }

  const includeAgents = async () => {
    const dialogBody = document.createElement('div');
    const footer = document.createElement('div');

    footer.innerHTML = `
      <gz-button id="includeAgentsAction">Include Agents</gz-button>
      <gz-button id="cancelAction" type="outline" color="secondary">Cancel</gz-button>
    `;

    dialogBody.innerHTML = `
      <p style="margin-bottom: 1rem;">This action will allow the Bitdefender agent to be installed on the selected endpoints.</p>
    `;

    const includeAgentsActionButton = footer.querySelector('#includeAgentsAction');
    const cancelActionButton = footer.querySelector('#cancelAction');

    const dialogConfigs = {
      headline: 'Include agents in sync?',
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(dialogConfigs);

    if (includeAgentsActionButton) {
      includeAgentsActionButton.addEventListener('click', async () => {
        dialog.dismiss()
        deleteData(ApiEndpoints.AGENT_EXCLUSION, selectedAgents.map(a => a.id))
        setTimeout(() => {
          gridElement.controller.handleGridItemsLoad(true, true);
        }, 1000);
      });
    }

    if (cancelActionButton) {
      cancelActionButton.addEventListener('click', () => { dialog.dismiss() })
    }
  }

  const handleAgentExclusions = async () => {
    if (areItemsExcluded) {
      await includeAgents()
    } else {
      await excludeAgents()
    }
  }

  function getScanDescription(typeOfScan) {
    const isQuick = typeOfScan === ScanTypeValues.Quick;
    const scanType = isQuick ? 'quick' : 'full system';
    const scanDetails = isQuick
      ? 'Quick scan is fast and targets key system areas and recent file changes to detect malware, rootkits, PUAs, and other threats.'
      : 'Full scan targets the entire system, including all drives and file types, to detect malware, rootkits, PUAs, and other threats.';
  
    return `
      <p style="margin-bottom: 1rem;">
        You are about to start the Bitdefender agent ${scanType} scan task on the selected targets.
      </p>
      <p style="margin-bottom: 1rem;">
        ${scanDetails}
      </p>
      <p style="margin-bottom: 1rem;">
        Only targets with the sync status <b>Installed</b> will be scanned.
      </p>
    `;
  }

  const handleScan = async (event) => {
    const dialogBody = document.createElement('div');
    const footer = document.createElement('div');
    const typeOfScan = event.detail.selected;

    footer.innerHTML = `
      <gz-button id="scanAgentsAction">Scan</gz-button>
      <gz-button id="cancelAction" type="outline" color="secondary">Cancel</gz-button>
    `;

    dialogBody.innerHTML = getScanDescription(typeOfScan);

    const scanAgentsActionButton = footer.querySelector('#scanAgentsAction');
    const cancelActionButton = footer.querySelector('#cancelAction');

    const dialogConfigs = {
      headline: typeOfScan === ScanTypeValues.Quick ? 'Start quick scan?' : 'Start full scan?',
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = window.globalThis.nirvana.dependencies.services.buildDialogElement(dialogConfigs);

    if (scanAgentsActionButton) {
      scanAgentsActionButton.addEventListener('click', async () => {
        dialog.dismiss();
        await createScanTaskAgents(selectedAgents.map(a => a.id), typeOfScan);
        setTimeout(() => {
          gridElement.controller.handleGridItemsLoad(true, true);
        }, 1000);
      });
    }

    if (cancelActionButton) {
      cancelActionButton.addEventListener('click', () => { dialog.dismiss() });
    }
  }

  const exclusionButtonTitle = areItemsExcluded ? "Include agents in sync" : "Exclude agents from sync"

  let agentExclusionButtonRef = useRef(null);
  useEventListener({ eventName: 'click', handler: handleAgentExclusions, ref: agentExclusionButtonRef });

  let syncAgentButtonRef = useRef(null);
  useEventListener({ eventName: 'click', handler: handleSyncAgents, ref: syncAgentButtonRef });

  let uninstallAgentsButtonRef = useRef(null)
  useEventListener({ eventName: 'click', handler: handleAgentsUninstall, ref: uninstallAgentsButtonRef });

  let scanButtonRef = useRef(null);
  useEventListener({ eventName: 'selectionChanged', handler: handleScan, ref: scanButtonRef });

  window.nirvana.translator = {
    translateFn: (agent) => agent
  };

  return (
    <gz-grid-view style={{ "height": "87vh" }}
      id="grid"
      showGlobalActions
    >
      <div className="header-buttons flex flex-row" slot="grid-header-actions-slot">
        <div className='mr-3'>
          <gz-button id='sync-agents-button' className="mr-4" ref={syncAgentButtonRef} {...(shouldDisableAgentSyncButton) ? { disabled: true } : {}}>Sync Agents</gz-button>
        </div>
        <div className='mr-3'>
          <gz-button id='exclusion-agents-button' type='quiet' color='secondary' className="mr-2" ref={agentExclusionButtonRef} {...(selectedAgents.length === 0) ? { disabled: true } : {}}>{exclusionButtonTitle}</gz-button>
        </div>
        <div className='mr-3'>
          <gz-select overlaypositionx="left" options={JSON.stringify(ScanTypes)} ref ={scanButtonRef} {...(selectedAgents.length === 0) ? { disabled: true } : {}}>
          <gz-button class="select-button" type="quiet" color="secondary" theme="light" buttonheight="medium" buttonfontsize="medium">
            <div class="d-flex align-items-center">
              scan
              <gz-icon name="ds-icon-dropdown_arrow" color="secondary" size="32"></gz-icon>
            </div>
          </gz-button>
          </gz-select>
        </div>
        <gz-button id='uninstall-agents-button' type='outline' color='negative' className="mr-2" ref={uninstallAgentsButtonRef} {...(selectedAgents.length === 0) ? { disabled: true } : {}}>Uninstall</gz-button>
      </div>
    </gz-grid-view>
  )
}
export default EndpointsGrid